/**
 * Captura PNG/JPG + snapshot de dados de painéis em iframe oculto.
 *
 * Por que: a aba principal só conhece snapshots de páginas que o usuário
 * visitou. Para o agendador funcionar sem visitas manuais, montamos um
 * iframe que carrega a rota do painel, espera o snapshot ficar disponível
 * em `iframe.contentWindow.__pharuerpSnapshots`, e em seguida usamos os
 * dados (para PDF/Excel/CSV) ou capturamos o DOM (para PNG/JPG).
 *
 * O iframe é destruído ao final (mesmo em caso de erro). Roda na mesma
 * origem da aba principal — herda cookies de auth e localStorage.
 */

import html2canvas from "html2canvas";
import { normalizarCoresParaCaptura } from "../exportacao/normalizarCores";

const IFRAME_WIDTH = 1366;
const IFRAME_HEIGHT = 900;
const DEFAULT_TIMEOUT_MS = 60000;
const POLL_INTERVAL_MS = 250;
// Tempo extra após snapshot aparecer, para layout/animações estabilizarem.
// 1500ms cobre renders pesados (tabelas com 100+ linhas + recharts).
const STABILIZATION_MS = 1500;
// Tempo máximo aguardando linhas em tabelas com dados não-vazios.
const TBODY_WAIT_MS = 5000;

function aguardar(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function montarIframe(rota) {
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.setAttribute("tabindex", "-1");
  iframe.style.cssText = `
    position: fixed;
    top: -100000px;
    left: 0;
    width: ${IFRAME_WIDTH}px;
    height: ${IFRAME_HEIGHT}px;
    border: 0;
    visibility: hidden;
    pointer-events: none;
  `;
  // ?export=1 sinaliza para a UI esconder elementos não-essenciais.
  const sep = rota.includes("?") ? "&" : "?";
  iframe.src = `${rota}${sep}export=1`;
  document.body.appendChild(iframe);
  return iframe;
}

/**
 * Atributos de aparência que o app grava no <html> (ThemeContext).
 * [data-theme] é o ÚNICO canal de tema: lightTheme e darkTheme são o mesmo
 * objeto de tokens `var(--*)` (styles/theme.jsx), então quem decide claro ou
 * escuro é o atributo no documento, não o React.
 */
const ATRIBUTOS_DE_APARENCIA = ["theme", "accent", "density", "animacoes"];

/**
 * Copia a aparência da aba para o <html> do iframe.
 *
 * O iframe oculto é um boot NOVO do app: ele resolve o tema sozinho
 * (localStorage -> ThemeContext -> hidratação do /api/me). Quando a
 * preferência do servidor discorda do que está valendo na aba, o iframe sobe
 * CLARO enquanto a aba está ESCURA — e a exportação do painel que está aberto
 * (capturado direto do DOM da aba) sai escura enquanto as dos outros painéis
 * saem claras, no mesmo lote, com minutos de diferença. A aba é a fonte da
 * verdade: espelhar aqui alinha o lote inteiro.
 */
function espelharAparencia(iframeDoc) {
  try {
    const origem = document.documentElement.dataset;
    const destino = iframeDoc.documentElement;
    ATRIBUTOS_DE_APARENCIA.forEach((chave) => {
      const valor = origem[chave];
      if (valor === undefined) {
        delete destino.dataset[chave];
      } else {
        destino.dataset[chave] = valor;
      }
    });
  } catch {
    // Aparência é cosmética: nunca derrubar a exportação por causa dela.
  }
}

function destruirIframe(iframe) {
  try {
    if (iframe && iframe.parentNode) {
      iframe.parentNode.removeChild(iframe);
    }
  } catch {
    // ignore
  }
}

async function esperarLoad(iframe, timeoutMs) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error("Iframe não carregou (timeout).")),
      timeoutMs
    );
    iframe.addEventListener(
      "load",
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true }
    );
    iframe.addEventListener(
      "error",
      () => {
        clearTimeout(timer);
        reject(new Error("Erro ao carregar iframe."));
      },
      { once: true }
    );
  });
}

/**
 * Detecta se o iframe redirecionou para `/login` (sessão expirada).
 */
function detectarRedirectLogin(iframe) {
  try {
    const path = iframe.contentWindow?.location?.pathname || "";
    return path.includes("/login") || path.includes("/primeiro-acesso");
  } catch {
    return false;
  }
}

/**
 * Quando o iframe redirecionou para login, capta o estado relevante e
 * monta uma mensagem mais útil para o usuário + log no console para
 * diagnóstico futuro (PJ — 2026-05-22).
 */
function montarErroRedirectLogin(iframe, painelId) {
  let pathname = "(indisponível)";
  let temSessaoLocal = false;
  try {
    pathname = iframe.contentWindow?.location?.pathname || "(vazio)";
  } catch {
    // cross-origin guard — improvável em same-origin, mas defensivo
  }
  try {
    const raw = iframe.contentWindow?.localStorage?.getItem(
      "pharuerp:access-session"
    );
    temSessaoLocal = Boolean(raw && raw !== "null");
  } catch {
    // localStorage indisponível — assumimos sem sessão
  }
  // eslint-disable-next-line no-console
  console.warn(
    "[iframeCapture] Iframe redirecionado para login. Diagnóstico:",
    { painelId, pathname, temSessaoLocal }
  );
  // Mensagem orientada à AÇÃO — vendedor vê isso no histórico do
  // agendamento e sabe o que fazer.
  return new Error(
    "Sessão expirada durante a exportação automática. " +
      "Abra a aba principal do PharuERP, faça login se necessário, " +
      "e o próximo lote agendado vai funcionar."
  );
}

/**
 * Aguarda o snapshot do painel aparecer no `window` do iframe.
 */
async function esperarSnapshotNoIframe(iframe, painelId, deadlineTs) {
  const inicioTs = Date.now();
  while (Date.now() < deadlineTs) {
    if (detectarRedirectLogin(iframe)) {
      throw montarErroRedirectLogin(iframe, painelId);
    }
    const win = iframe.contentWindow;
    const mapa = win?.__pharuerpSnapshots;
    if (mapa && typeof mapa.get === "function") {
      const snap = mapa.get(painelId);
      // pronto !== false: páginas que publicam `pronto` no payload só são
      // aceitas com dados carregados. Sem o marcador (undefined), mantém o
      // comportamento antigo — mas as 5 telas com snapshot próprio agora
      // registram pronto e NÃO publicam mais o 1º render vazio (race que
      // gerava Excel/CSV agendado só com Resumo zerado).
      if (
        snap &&
        snap.payload &&
        snap.payload.secoes &&
        snap.payload.pronto !== false
      ) {
        return snap;
      }
    }
    // eslint-disable-next-line no-await-in-loop
    await aguardar(POLL_INTERVAL_MS);
  }
  // deadlineTs - inicio = orçamento real de espera; a versão anterior
  // imprimia o epoch em segundos ("não apareceu em 1787654321s").
  throw new Error(
    `Snapshot do painel "${painelId}" não apareceu em ${Math.round(
      (deadlineTs - inicioTs) / 1000
    )}s.`
  );
}

/**
 * Aguarda elementos especificos no DOM do iframe ficarem renderizados.
 */
async function esperarElementosProntos(iframeDoc, selectors, deadlineTs) {
  while (Date.now() < deadlineTs) {
    const encontrados = selectors
      .map((sel) => iframeDoc.querySelector(sel))
      .filter(Boolean);
    const visiveis = encontrados.filter((el) => {
      const r = el.getBoundingClientRect();
      return r.width > 4 && r.height > 4;
    });
    if (visiveis.length === selectors.length) return visiveis;
    // eslint-disable-next-line no-await-in-loop
    await aguardar(POLL_INTERVAL_MS);
  }
  throw new Error("Conteúdo visual não ficou pronto a tempo.");
}

/**
 * Aguarda elementos com `<tbody>` filho terem PELO MENOS UMA linha
 * renderizada. Defensa contra captura de tabelas em meio à animação
 * de aparecimento (framer-motion). Usa timeout curto — se a tabela
 * realmente é vazia, prossegue.
 *
 * `expectedRows` é o número esperado de linhas (vindo do snapshot).
 * Se conhecido, esperamos o DOM ter pelo menos esse número (ou metade,
 * tolerância para virtualização).
 */
async function esperarLinhasNasTabelas(elementos, expectedTotalRows) {
  const inicio = Date.now();
  const meta = Math.max(1, Math.floor((expectedTotalRows || 1) / 2));
  while (Date.now() - inicio < TBODY_WAIT_MS) {
    let totalRows = 0;
    for (const el of elementos) {
      const tbodies = el.querySelectorAll("tbody");
      for (const tb of tbodies) {
        totalRows += tb.querySelectorAll("tr").length;
      }
    }
    if (expectedTotalRows === 0) return; // tabela genuinamente vazia
    if (totalRows >= meta) return;
    // eslint-disable-next-line no-await-in-loop
    await aguardar(POLL_INTERVAL_MS);
  }
  // Timeout — segue mesmo assim. Captura pode ficar incompleta, mas
  // pelo menos tenta.
}

function totalDeRowsEsperadas(snapshot) {
  const secoes = snapshot?.payload?.secoes || [];
  return secoes.reduce((acc, s) => acc + (s.rows?.length || 0), 0);
}

/**
 * Versão genérica de captura via iframe. Retorna { snapshot, canvases? }.
 *
 * - Se `capturarDom` é true, também tenta capturar os elementos via
 *   html2canvas — útil para PNG/JPG.
 * - Se false, retorna apenas o snapshot (mais rápido, suficiente para
 *   PDF/Excel/CSV).
 */
export async function abrirPainelEmIframe({
  rota,
  painelId,
  capturarDom = false,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  // Fundo aplicado pelo html2canvas em áreas transparentes — tema do
  // usuário quando informado; default preserva o comportamento anterior.
  backgroundColor = "#ffffff",
}) {
  if (!rota || !painelId) {
    throw new Error("abrirPainelEmIframe: rota e painelId são obrigatórios.");
  }

  const iframe = montarIframe(rota);
  const deadline = Date.now() + timeoutMs;

  try {
    await esperarLoad(iframe, Math.max(timeoutMs - 5000, 10000));

    if (detectarRedirectLogin(iframe)) {
      throw montarErroRedirectLogin(iframe, painelId);
    }

    const iframeDoc = iframe.contentDocument;
    if (!iframeDoc) {
      throw new Error("iframe sem contentDocument (cross-origin?).");
    }

    // Espera o snapshot ficar disponível (significa que os dados
    // chegaram e a página renderizou os componentes esperados).
    const snapshot = await esperarSnapshotNoIframe(iframe, painelId, deadline);

    if (!capturarDom) {
      return { snapshot };
    }

    // Para PNG/JPG: alinha a aparência com a aba, ativa export-mode e captura.
    // O espelhamento tem de vir ANTES do export-mode para que a troca de
    // tokens e a neutralização de animação valham sobre o mesmo tema.
    espelharAparencia(iframeDoc);
    await aguardar(60);

    const visual = snapshot.payload?.visual;
    const sections = visual?.sections || [];
    const selectors = sections.map((s) => s.selector).filter(Boolean);
    if (selectors.length === 0) {
      throw new Error(
        `Snapshot do painel "${painelId}" não declara seções visuais.`
      );
    }

    // Aguarda elementos visuais ficarem renderizados (já que o snapshot
    // pode aparecer antes do DOM estabilizar com todos os data-pdf).
    const elementos = await esperarElementosProntos(
      iframeDoc,
      selectors,
      deadline
    );

    // Largura de layout ANTES do export-mode. As regras de captura zeram o
    // padding da sidebar e o max-width do container: sem fixar, o conteúdo
    // reflui mais largo e as colunas flexíveis da tabela absorvem a sobra,
    // saindo esticadas. Mesma correção do caminho manual (imageExporter).
    const larguraDeLayout = new Map();
    elementos.forEach((el) => {
      if (el) larguraDeLayout.set(el, Math.round(el.getBoundingClientRect().width));
    });

    iframeDoc.body.setAttribute("data-export-mode", "true");

    // Aguarda tbody ter linhas (framer-motion entrega após delay).
    const expectedRows = totalDeRowsEsperadas(snapshot);
    await esperarLinhasNasTabelas(elementos, expectedRows);

    // Pausa para layout/animations residuais estabilizarem.
    await aguardar(STABILIZATION_MS);

    const canvases = [];
    for (const el of elementos) {
      // scale 1.5 vs 2: ~44% menos pixels totais. Ainda nítido em monitores
      // padrão (96 DPI) e mantém texto legível. Reduz tamanho de arquivo
      // significativamente para PNG/JPG de painéis com 50+ linhas.
      // eslint-disable-next-line no-await-in-loop
      const largura = larguraDeLayout.get(el);
      const larguraInline = el.style.width;
      const maxWidthInline = el.style.maxWidth;
      if (largura) {
        el.style.width = `${largura}px`;
        el.style.maxWidth = `${largura}px`;
      }
      // Mesmo motivo do caminho manual: cores modernas derrubam o html2canvas.
      const restaurarCores = normalizarCoresParaCaptura(el);
      let canvas;
      try {
        // eslint-disable-next-line no-await-in-loop
        canvas = await html2canvas(el, {
          scale: 1.5,
          useCORS: true,
          backgroundColor,
          logging: false,
          width: Math.max(el.scrollWidth, el.offsetWidth),
          height: Math.max(el.scrollHeight, el.offsetHeight),
          windowWidth: iframe.contentWindow.innerWidth,
          windowHeight: iframe.contentWindow.innerHeight,
        });
      } finally {
        restaurarCores();
        if (largura) {
          el.style.width = larguraInline;
          el.style.maxWidth = maxWidthInline;
        }
      }
      canvases.push(canvas);
    }
    return { snapshot, canvases };
  } finally {
    destruirIframe(iframe);
  }
}
